// SignalCard (.sig del handoff) — card de señal reutilizable + variante lock.
// Edge en PUNTOS ("Edge −15.4"), pick "UNDER 171.5" (inglés), stake "2u",
// cuota solo si el motor la trae (si no, la columna no existe).
import { useNavigate } from 'react-router-dom';

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
);

export function fmtEdge(edge) {
  const e = parseFloat(edge);
  if (isNaN(e)) return null;
  return `Edge ${e > 0 ? '+' : e < 0 ? '−' : ''}${Math.abs(e).toFixed(1)}`;
}

export function pickOf(signal) {
  const side = String(signal.signal_type || '').toUpperCase().includes('OVER') ? 'OVER' : 'UNDER';
  const line = signal.line != null && signal.line !== '' ? signal.line : '?';
  return `${side} ${line}`;
}

export function matchOf(signal) {
  if (signal.away_team && signal.home_team) return `${signal.away_team} vs ${signal.home_team}`;
  const p = String(signal.partido || '?');
  return p.includes(' @ ') ? p.replace(' @ ', ' vs ') : p;
}

const HB_RE = /handball|hbl|ehf|asobal|starligue|handbold|handboll/i;

export function sportChipOf(signal) {
  const esHb = signal.match_type === 'handball' || signal.sport === 'handball'
    || HB_RE.test(String(signal.league || signal.liga || ''));
  return esHb ? '🤾 Handball' : '🏀 Básquet';
}

export default function SignalCard({ signal, locked = false, radar = false }) {
  const navigate = useNavigate();
  const liga = signal.league || signal.liga || '—';
  const stake = `${parseFloat(signal.stake_u_publicado || 1) || 1}u`.replace('.0u', 'u');
  const cuota = signal.cuota || signal.odds || null;
  const edgeTxt = fmtEdge(signal.edge);

  // Variante RADAR (paridad 2026-09-03): alertas del evaluador EG1 PDF
  // (/radar_vip_results) con la MISMA regla de estados del dashboard
  // (useActiveSignals): EN CURSO + línea live == línea del snapshot →
  // "🟢 ENTRADA ACTIVA"; en curso con línea movida → "🟡 VIGILAR";
  // resuelta → estilo normal con W/L. Marcador/tiempo live vía prop
  // signal.live (nodo /live_games) mientras el partido siga.
  const enCurso = radar && !signal.resolved && !!signal.live;
  const lineaVigente = enCurso && signal.live.lineaNow != null && signal.line != null
    && Math.abs(parseFloat(signal.live.lineaNow) - parseFloat(signal.line)) < 0.01;
  const chipMuted = { background: 'var(--bg)', color: 'var(--muted)' };
  const m = (enCurso && signal.live.marcador) || null;

  const body = (
    <>
      <div className="t">
        <span className="lg">{liga}</span>
        <span className="edge" style={chipMuted}>{sportChipOf(signal)}</span>
        {radar && !enCurso && <span className="edge" style={chipMuted}>📡 RADAR</span>}
        {enCurso && (
          <span className="edge" style={lineaVigente
            ? { background: 'rgba(52,199,89,.12)', color: '#1DA84C' }
            : { background: 'rgba(255,204,0,.14)', color: '#B78B00' }}>
            {lineaVigente ? '🟢 ENTRADA ACTIVA' : '🟡 VIGILAR'}
          </span>
        )}
        {edgeTxt && <span className="edge">{edgeTxt}</span>}
      </div>
      <p className="match">{matchOf(signal)}</p>
      <div className="meta">
        <div className="c"><p className="l">Pick</p><p className="v g">{pickOf(signal)}</p></div>
        {cuota && <div className="c"><p className="l">Cuota</p><p className="v">{cuota}</p></div>}
        {radar
          ? <div className="c"><p className="l">Ventana</p><p className="v">{signal.ventana || '—'}</p></div>
          : <div className="c"><p className="l">Stake</p><p className="v">{stake}</p></div>}
        {enCurso && m && (
          <div className="c"><p className="l">Marcador</p><p className="v">{m.away ?? '–'}-{m.home ?? '–'}</p></div>
        )}
        {enCurso && (
          <div className="c"><p className="l">Tiempo</p>
            <p className="v">{signal.live.periodo ? `Q${signal.live.periodo}` : '—'} {signal.live.reloj || ''}</p></div>
        )}
        {radar && signal.resolved && (
          <div className="c"><p className="l">Resultado</p>
            <p className={`v ${signal.won ? 'g' : ''}`}>{signal.won ? 'WIN' : 'LOSS'}</p></div>
        )}
        {!locked && <div className="arw"><Chevron /></div>}
      </div>
    </>
  );

  if (locked) {
    return (
      <div className="app-sig lock">
        <div className="blur">{body}</div>
        <div className="app-lockov">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          <p>1 señal más disponible con tu plan activo</p>
          <button onClick={() => navigate('/checkout')}>Activar acceso</button>
        </div>
      </div>
    );
  }

  return (
    <button
      className={`app-sig${enCurso ? ' act' : ''}`}
      onClick={() => navigate(`/app/partido/${signal.id}`, { state: { signal } })}
    >
      {body}
    </button>
  );
}
